import { computed, type InjectionKey } from 'vue';
import { createStore, Store, useStore as baseUseStore } from 'vuex';
import {
    Alert,
    ConfirmModal,
    Facility,
    FacilityIntegrationSettings,
    JwtPayload,
    Modal,
    Patient,
    PatientInviteSummaryViewModel,
    QueryResultViewModel,
    SelectItem,
    Staff,
    StaffAlertSummaryViewModel,
    WorkflowContent
} from "@/store/classes";
import axios from "axios";
import { DateTime, Settings } from "luxon";
import { ContactMethod, ExternalFacilityType, MessagingFeature, Role } from "@/store/enums";
import { PatientModule, type PatientModuleState } from '@/store/PatientModule';
import { globalT } from '@/config/i18n';
import { ToastServiceMethods } from 'primevue/toastservice';


const TICKER_INTERVAL = 10000;
const DEFAULT_CLIENT_SIDE_TIMEOUT = 20;
const BREAKPOINT_MOBILE = 960;
let tickerPointer: number | undefined = undefined;
let toastService: ToastServiceMethods;

declare let pendo: {
    initialize: (config: Record<string, unknown>) => void;
};

const initialState = {
    accessToken: {} as JwtPayload,
    initialized: false,
    loaders: [] as string[],
    currentModal: {} as Modal,
    currentConfirmModal: {} as Modal,
    alerts: [] as Alert[],
    sessionTicker: null as null | Date,
    sessionLocked: false,
    patientId: null as null | number,
    currentPatient: {} as Patient,
    hasReputationIntegration: false,
    facilityId: null as null | number,
    facility: {
        featureSettings: {}
    } as Facility,
    facilityLocations: [] as Facility[],
    facilityFeatureFlags: [] as string[],
    staffId: null as null | number,
    currentStaff: {} as Staff,
    staff: [] as Staff[],
    staffAlertSummary: {
        messageAlertCount: 0,
        messageLatestAlertDate: undefined,
        surveyAlertCount: 0,
        surveyLatestAlertDate: undefined,
        taskAlertCount: 0,
        taskLatestAlertDate: undefined
    } as StaffAlertSummaryViewModel,
    unreadConversationCount: 0,
    openVisitRequestCount: 0,
    workflowContent: [] as WorkflowContent[],
    enablePatientManagement: false,
    isMobile: false,
};
type AppState = typeof initialState;

export type RootState = AppState & {
    // Modules go here
    PatientModule?: PatientModuleState;
};

export const key: InjectionKey<Store<RootState>> = Symbol('root-store');

const store = createStore<RootState>({
    state: initialState,
    getters: {
        isLoading(state): boolean {
            return !!state.loaders?.length;
        },
        // flags
        isCaseTagFacility(state): boolean {
            return (state.facilityFeatureFlags.indexOf('contact-group-case-tags') > -1)
                && state.facility.externalFacilityType === ExternalFacilityType.TherapyC;
        },
        isDigitalIntakeFacility(state): boolean {
            return state.facilityFeatureFlags.indexOf('digital-intake-surveys') > -1;
        },
        hasVisitRemindersFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('visit-reminders') > -1;
        },
        hasVisitRequestsFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('visit-requests') > -1;
        },
        hasConfigurableIntakeFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('configurable-intake') > -1;
        },
        hasIntakeCaseInfoFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('intake-case-info') > -1;
        },
        hasVisitReminderScheduledOptionsFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('visit-reminders-scheduled-options') > -1;
        },
        hasVisitReminderCheckInFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('visit-reminders-check-in') > -1;
        },
        hasSpanishLanguageSupportFeatureFlag(state): boolean {
            return state.facilityFeatureFlags.indexOf('spanish-language-support') > -1;
        },

        // staff getters
        isStandaloneFacility(state): boolean {
            return state.facility.externalFacilityType === ExternalFacilityType.NhMessaging;
        },
        isStandaloneUserFacility(state): boolean {
            return state.facility.externalFacilityType === ExternalFacilityType.NhMessaging
                || state.facility.externalFacilityType === ExternalFacilityType.TherapyC
                || !!state.patientId;
        },
        isFacilityAdmin(state): boolean {
            return state.currentStaff?.staffRoles && state.currentStaff.staffRoles.some(x => x.role === Role.SystemAdmin || x.role == Role.FacilityAdmin);
        },
        isSystemAdmin(state): boolean {
            return state.currentStaff?.staffRoles && state.currentStaff.staffRoles.some(x => x.role === Role.SystemAdmin);
        },
        staffItems(state): SelectItem[] {
            return state.staff.map(x =>
                ({ text: x.firstName + ' ' + x.lastName, value: x.id }) as SelectItem);
        },
        facilityLocationItems(state): SelectItem[] {
            return state.facilityLocations.map(x =>
                ({ text: x.facilityName, value: x.id }) as SelectItem);
        },

        // labels
        facilityLabelKey(state): string {
            return state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
        },
        facilityLabel(state): string {
            const facilityKey = state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
            return globalT(`facilityTerms.Facility.${facilityKey}`);
        },
        facilitiesLabel(state): string {
            const facilityKey = state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
            return globalT(`facilityTerms.Facilities.${facilityKey}`);
        },
        facilityLocationLabel(state): string {
            const facilityKey = state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
            return globalT(`facilityTerms.Location.${facilityKey}`);
        },
        facilityLocationsLabel(state): string {
            const facilityKey = state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
            return globalT(`facilityTerms.Locations.${facilityKey}`);
        },
        treatmentCycleLabel(state): string {
            const facilityKey = state.facility.externalFacilityType === ExternalFacilityType.TherapyC ? 'therapyC' : 'default';
            return globalT(`facilityTerms.TreatmentCycle.${facilityKey}`);
        },
    },
    mutations: {
        setAccessToken(state, accessToken: string) {
            if (accessToken) {
                const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
                state.accessToken = {
                    value: accessToken,
                    username: tokenPayload.UserName,
                    appName: tokenPayload.application,
                    tokenExpiration: DateTime.fromSeconds(tokenPayload.exp).toJSDate()
                };
            } else {
                state.accessToken = {
                    value: '',
                    username: '',
                    appName: '',
                    tokenExpiration: new Date()
                };
            }
        },
        setInitialized(state, initialized: boolean) {
            state.initialized = initialized;
        },
        setIsMobile(state, isMobile: boolean) {
            state.isMobile = isMobile;
        },
        addLoader(state, loaderName: string) {
            state.loaders = [...state.loaders, loaderName];
        },
        removeLoader(state, loaderName: string) {
            state.loaders = state.loaders.filter(x => x !== loaderName);
        },
        setModal(state, modal: Modal) {
            state.currentModal = modal;
        },
        setConfirmModal(state, modal: Modal) {
            state.currentConfirmModal = modal;
        },
        refreshTicker(state) {
            state.sessionTicker = new Date();
        },
        clearTicker(state) {
            state.sessionTicker = null;
        },
        setSessionLocked(state, sessionLocked) {
            state.sessionLocked = sessionLocked;
        },
        setFacilityId(state, facilityId: number) {
            state.facilityId = facilityId;
        },

        // portal setters
        setPatientId(state, patientId: number) {
            state.patientId = patientId;
        },
        setCurrentPatient(state, patient: Patient) {
            state.currentPatient = patient;
        },

        // staff setters
        setFacilityFeatureFlags(state, featureFlags: string[]) {
            state.facilityFeatureFlags = featureFlags;
        },
        setStaffId(state, staffId: number) {
            state.staffId = staffId;
        },
        setCurrentStaff(state, staff: Staff) {
            state.currentStaff = staff;
        },
        setStaff(state, staff: Staff[]) {
            state.staff = staff;
        },
        setStaffAlertSummary(state, staffAlertSummary: StaffAlertSummaryViewModel) {
            state.staffAlertSummary = staffAlertSummary;
        },
        addAlert(state, alert: Alert) {
            state.alerts.push(alert);
        },
        clearAlerts(state) {
            state.alerts = [];
        },
        setPatientVisitRequestSummary(state, visitRequestCount: number) {
            state.openVisitRequestCount = visitRequestCount;
        },
        setHasReputationIntegration(state, hasReputationIntegration: boolean) {
            state.hasReputationIntegration = hasReputationIntegration;
        },
        setFacility(state, facility: Facility) {
            state.facility = facility;
            Settings.defaultZoneName = facility.ianaTimeZoneName || 'system';
            if (!facility.clientSideTimeout) {
                state.facility.clientSideTimeout = DEFAULT_CLIENT_SIDE_TIMEOUT;
            }
            if (facility.externalFacilityType === ExternalFacilityType.NhMessaging) {
                state.enablePatientManagement = true;
            }
        },
        setFacilityLocations(state, facilityLocations: Facility[]) {
            state.facilityLocations = facilityLocations;
        },
        setWorkflowContent(state, workflowContent: WorkflowContent[]) {
            state.workflowContent = workflowContent;
        },
        setPatientManagement(state, enablePatientManagement) {
            state.enablePatientManagement = enablePatientManagement;
        },
        setUnreadConversationCount(state, count) {
            state.unreadConversationCount = count;
        }
    },
    actions: {
        setAccessToken({ commit }, accessToken: string): void {
            const tokenKey = process.env.VUE_APP_ACCESS_TOKEN_KEY;
            if (tokenKey) {
                window.localStorage.setItem(tokenKey, JSON.stringify(accessToken));
            }
            commit('setAccessToken', accessToken);
        },
        showLoader({ commit }, loaderName: string): void {
            commit('addLoader', loaderName);
        },
        hideLoader({ commit }, loaderName: string): void {
            commit('removeLoader', loaderName);
        },
        async showModal({ commit }, modal: Modal): Promise<unknown> {
            return await new Promise((resolve) => {
                const props = { ...modal.props, resolve };
                commit('setModal', { ...modal, props });
            });
        },
        async showConfirmModal({ commit }, modal: ConfirmModal): Promise<boolean> {
            return await new Promise((resolve) => {
                commit('setConfirmModal', {
                    name: modal.modalName ?? 'ConfirmModal',
                    props: {
                        ...modal,
                        resolve
                    }
                });
            });
        },
        hideConfirmModal({ commit }): void {
            commit('setConfirmModal', {});
        },
        hideModal({ commit }): void {
            commit('setModal', {});
        },
        showAlert({ commit }, alert: Alert): void {
            toastService.add({
                severity: alert.type === 'warning' ? 'warn' : alert.type as any,
                detail: alert.text,
                closable: false,
                contentStyleClass: 'opacity-100',
                life: 4000
            });
        },
        hideAlert({ commit }): void {
            toastService.removeAllGroups();
        },
        startTicker({ commit }): void {
            if (!tickerPointer) {
                commit('refreshTicker');
                tickerPointer = window.setInterval(() => {
                    commit('refreshTicker');
                }, TICKER_INTERVAL);
            }
        },
        haltTicker({ commit }): void {
            window.clearInterval(tickerPointer);
            tickerPointer = undefined;
            commit('clearTicker');
        },
        setSessionLocked({ commit }, sessionLocked): void {
            commit('setSessionLocked', sessionLocked);
        },
        setFacilityId({ commit }, facilityId: number): void {
            commit('setInitialized', false);
            window.localStorage.setItem(process.env.VUE_APP_FACILITY_ID_KEY!, JSON.stringify(facilityId));
            commit('setFacilityId', facilityId);
        },

        // portal actions

        async initializePortal({ commit }, { contactKey, facility }): Promise<void> {
            commit('setFacilityId', facility.id);
            commit('setFacility', facility);
            pendo.initialize({
                visitor: {
                    id: 'nhportal-' + contactKey,
                    integration: facility.externalFacilityType
                },
                account: {
                    id: 'nhmessaging-' + facility.id,
                    name: facility.facilityName
                },
                guides: {
                    disabled: true
                }
            });
        },
        async initializePatientPortal({ commit, dispatch, state }): Promise<void> {
            if (state.initialized) return;
            await dispatch('loadCurrentPatient');
            commit('setIsMobile', window.innerWidth < BREAKPOINT_MOBILE)

            // load portal facility data here
            await Promise.all([
                dispatch('loadPortalFacility'),
                dispatch('loadPortalFacilityFeatureFlags')
            ]);

            pendo.initialize({
                visitor: {
                    id: 'nhportal-' + state.currentPatient.id,
                    username: state.accessToken.username,
                    integration: state.facility.externalFacilityType
                },
                account: {
                    id: 'nhmessaging-' + state.facility.id,
                    name: state.facility.facilityName
                },
                guides: {
                    disabled: true
                }
            });
            commit('setInitialized', true);
            dispatch('startTicker');
        },
        setPatientId({ commit }, patientId: number): void {
            commit('setPatientId', patientId);
        },
        async loadCurrentPatient({ commit, state }): Promise<void> {
            const response = await axios.get<Patient>(`/portal/patients/${state.patientId}`);
            if (response.data) {
                commit('setCurrentPatient', response.data);
            }
        },
        async loadPortalFacility({ commit, state }): Promise<void> {
            const response = await axios.get<Facility>('/portal/facilities/' + state.facilityId);
            if (response.data) {
                commit('setFacility', response.data);
            }
        },
        async loadPortalFacilityFeatureFlags({ commit, state }): Promise<void> {
            const response = await axios.get<Facility>(`/portal/facilities/${state.facilityId}/feature-flags`);
            if (response.data) {
                commit('setFacilityFeatureFlags', response.data);
            }
        },

        // staff actions

        async initialize({ commit, dispatch, getters, state }): Promise<void> {
            if (state.initialized) return;
            await dispatch('loadCurrentStaff');
            
            await Promise.all([
                dispatch('loadStaff'),
                dispatch('loadFacility'),
                dispatch('loadFacilityFeatureFlags'),
                dispatch('loadFacilityLocations'),
                dispatch('loadWorkflowContent'),
                dispatch('loadIntegrations'),
            ]);
            
            pendo.initialize({
                visitor: {
                    id: 'nhmessaging-' + state.currentStaff.id,
                    email: state.currentStaff.contactAddresses?.find(x => x.contactMethod === ContactMethod.Email)?.address,
                    name: state.currentStaff.firstName + ' ' + state.currentStaff.lastName,
                    username: state.accessToken.username,
                    FacilityAdmin: getters.isFacilityAdmin,
                    integration: state.facility.externalFacilityType
                },
                account: {
                    id: 'nhmessaging-' + state.facility.id,
                    name: state.facility.facilityName
                }
            });
            commit('setIsMobile', window.innerWidth < BREAKPOINT_MOBILE)
            commit('setInitialized', true);
            dispatch('startTicker');
        },
        initializeToast({ commit }, toast: ToastServiceMethods): void {
            toastService = toast;
        },
        setStaffId({ commit }, staffId: number): void {
            commit('setStaffId', staffId);
        },
        async loadCurrentStaff({ commit, state }): Promise<void> {
            // TODO error handling
            const response = await axios.get<Staff>(`/staff/${state.staffId}`);
            if (response.data) {
                commit('setCurrentStaff', response.data);
            }
        },
        async loadStaff({ commit }): Promise<void> {
            const response = await axios.get<QueryResultViewModel<Staff>>('/staff/query', {
                params: { excludeRoles: [Role.SystemAdmin, Role.ReadOnly, Role.FacilityFilteredAccess, Role.ReportAccess] }
            });
            if (response.data?.items) {
                commit('setStaff', response.data.items);
            }
        },
        async loadStaffAlertSummary({ commit, state }): Promise<void> {
            const response = await axios.get<StaffAlertSummaryViewModel>('/alerts/summary', {
                params: { staffId: state.staffId }
            });
            if (response.data) {
                commit('setStaffAlertSummary', response.data);
            }
        },
        async loadPatientVisitSummary({ commit }): Promise<void> {
            const response = await axios.get<number>('/visit-requests/summary');
            if (response.data) {
                commit('setPatientVisitRequestSummary', response.data);
            }
        },
        async loadConversationsSummary({ commit }): Promise<void> {
            const response = await axios.get<number>('/conversations/summary');
            if (response.data > -1) {
                commit('setUnreadConversationCount', response.data);
            }
        },
        async loadFacility({ commit, state }): Promise<void> {
            // TODO error handling
            const response = await axios.get<Facility>('/facilities/' + state.facilityId);
            if (response.data) {
                commit('setFacility', response.data);
            }
        },
        async loadFacilityFeatureFlags({ commit, state }): Promise<void> {
            const response = await axios.get<string[]>(`/facilities/${state.facilityId}/feature-flags`);
            if (response.data) {
                commit('setFacilityFeatureFlags', response.data);
            }
        },
        async loadFacilityLocations({ commit, state }): Promise<void> {
            // TODO error handling
            const response = await axios.get<QueryResultViewModel<Facility>>('/facilities/query', {
                params: { parentFacilityId: state.facilityId }
            });
            if (response.data?.items) {
                commit('setFacilityLocations', response.data.items);
            }
        },
        async loadWorkflowContent({ commit }): Promise<void> {
            // TODO error handling
            const response = await axios.get<QueryResultViewModel<WorkflowContent>>('/workflows/content/query', {
                params: { includeMessagingFeatures: [MessagingFeature.ReputationRequest] }
            });
            if (response.data?.items) {
                commit('setWorkflowContent', response.data.items);
            }
        },

        async loadIntegrations({ commit }): Promise<void> {
            const settingsResponse = await axios.get<FacilityIntegrationSettings[]>('/facility/settings/integration/');
            if (settingsResponse.data) {
                const hasReputationIntegration = settingsResponse.data.some(x => x.messagingFeature === MessagingFeature.ReputationRequest);
                commit('setHasReputationIntegration', hasReputationIntegration);
            }
        }
    },
    modules: {
        PatientModule
    }
});

export default store;
export const useRootStore = () => baseUseStore<RootState>(key);

window.addEventListener('resize', () => {
    store.commit('setIsMobile', window.innerWidth < BREAKPOINT_MOBILE)
});

export const useModal = (name: string) => {
    return (props: Record<string, unknown> = {}) => store.dispatch('showModal', { name, props });
}

export const useLoaders = (loaderName: string) => ({
    showLoader: () => store.dispatch('showLoader', loaderName),
    hideLoader: () => store.dispatch('hideLoader', loaderName),
    isLoading: computed(() => store.getters.isLoading)
});